import {
  Box,
  Card,
  Flex,
  Grid,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";

import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tooltip } from "@/components/ui/tooltip";
import { Badge } from "@/types";
import { getIconPathFromBadgeId } from "@/utils";

const SelectedBadgeCard = ({ badge }: { badge: Badge }) => (
  <Card.Root bgColor="bg.subtle" borderColor="bg.subtle" h={112}>
    <Card.Body>
      <HStack gap={4}>
        <Image
          boxSize={16}
          src={getIconPathFromBadgeId(badge.id, Boolean(badge.earnedAt))}
        />
        <VStack alignItems="start">
          <Text fontSize="lg" fontFamily="Lufga, sans-serif">
            {badge.title}
          </Text>
          <Text color="fg.muted">{badge.description}</Text>
        </VStack>
      </HStack>
    </Card.Body>
  </Card.Root>
);

const BadgesModalContent = ({ badges }: { badges: Badge[] }) => {
  const [selectedBadge, setSelectedBadge] = useState(badges[0]);

  return (
    <DialogContent borderRadius={"3xl"} pb={4} w={600} maxW={600}>
      <DialogHeader pt={8} px={8}>
        <VStack gap={3} alignItems="center" w="full">
          <Heading size="4xl" fontFamily="Lufga, sans-serif">
            Show Your Goon Flare!
          </Heading>
          <Text
            fontSize="lg"
            color="fg"
            fontFamily="Lufga, sans-serif"
            fontWeight={500}
          >
            Hover over a badge to see details
          </Text>
          <Text
            color="fg.muted"
            fontSize="md"
            fontWeight={500}
            textAlign="center"
          >
            Some badges might take time to show up while we catch up on all the
            activity. Thanks for your patience!
          </Text>
        </VStack>
        <DialogCloseTrigger mt={4} mr={4} />
      </DialogHeader>
      <DialogBody w={600} px={8} pb={8}>
        <VStack gap={8} alignItems="stretch">
          <SelectedBadgeCard badge={selectedBadge} />
          <SimpleGrid columns={[7]} gap={2} justifyItems="center">
            {badges.map((badge) => {
              const isSelectedBadge = badge.id === selectedBadge.id;
              return (
                <Box
                  key={badge.id}
                  boxSizing="border-box"
                  borderRadius="full"
                  outlineStyle={isSelectedBadge ? "dashed" : "initial"}
                  outlineWidth={isSelectedBadge ? "thin" : "none"}
                  outlineColor={"border.inverted"}
                  onMouseEnter={() => setSelectedBadge(badge)}
                  onFocus={() => setSelectedBadge(badge)}
                  position="relative"
                >
                  <Box
                    borderRadius="full"
                    top={0}
                    right={0}
                    boxSize={16}
                    position="absolute"
                    _hover={{
                      bgColor: "fg",
                      opacity: 0.1,
                    }}
                  />
                  <Box borderRadius="full" p={0}>
                    <Image
                      src={getIconPathFromBadgeId(
                        badge.id,
                        Boolean(badge.earnedAt),
                      )}
                      boxSize={16}
                    />
                  </Box>
                </Box>
              );
            })}
          </SimpleGrid>
        </VStack>
      </DialogBody>
    </DialogContent>
  );
};

export const BadgesCard = ({ badges }: { badges: Badge[] }) => {
  if (!badges) return null;

  return (
    <DialogRoot size="md">
      <DialogTrigger asChild>
        <Card.Root
          flex={1}
          borderRadius={"2xl"}
          cursor="pointer"
          className="group"
          _hover={{ transform: "scale(0.985)", transition: "transform 0.3s" }}
          display="flex"
          flexDirection="column"
          height="100%"
        >
          <Card.Header>
            <HStack justifyContent="space-between">
              <Card.Title
                fontFamily="Lufga, sans-serif"
                fontWeight={"700"}
                fontSize={"2xl"}
              >
                <HStack>
                  <Text>Badges</Text>
                </HStack>
              </Card.Title>
            </HStack>
          </Card.Header>
          <Card.Body marginTop="auto" justifyContent="flex-end">
            <Grid
              gap={2}
              templateColumns="repeat(auto-fill, minmax(78px, 1fr))"
            >
              {badges
                ?.sort(
                  (a, b) =>
                    // sort newest earned badges first
                    (b.earnedAt ? new Date(b.earnedAt).getTime() : 0) -
                    (a.earnedAt ? new Date(a.earnedAt).getTime() : 0),
                )
                .slice(0, 11)
                .map(({ id: badgeId, earnedAt, title }) => (
                  <Tooltip
                    key={badgeId}
                    content={title}
                    openDelay={100}
                    closeDelay={100}
                  >
                    <Image
                      src={getIconPathFromBadgeId(badgeId, Boolean(earnedAt))}
                      alt={badgeId}
                      boxSize={16}
                    />
                  </Tooltip>
                ))}
              {badges?.length > 11 && (
                <Flex boxSize={78} justifyContent="center">
                  <Text color="fg.subtle" alignSelf="center">
                    +{badges.length - 11}
                  </Text>
                </Flex>
              )}
            </Grid>
          </Card.Body>
        </Card.Root>
      </DialogTrigger>
      <BadgesModalContent badges={badges} />
    </DialogRoot>
  );
};
